import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Chip,
  alpha,
  useTheme,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from '@mui/material'
import {
  School,
  Assignment,
  EmojiEvents,
  TrendingUp,
  PlayCircle,
} from '@mui/icons-material'
import { AppDispatch, RootState } from '../app/store'
import {
  fetchUserEnrollments,
  startAssignment,
} from '../features/assignments/assignmentSlice'
import { fetchAssessments } from '../features/assessments/assessmentSlice'
import { UserEnrollment } from '../types'
import WelcomeCard from '../components/WelcomeCard'
import StatsCard from '../components/StatsCard'
import { LearningActivityChart } from '../components/LearningActivityChart'
import logger from '../utils/logger'
import ProgressChart from '../components/ProgressChart'

// Available Content Item Component (for Courses and Assessments)
const AvailableContentItem: React.FC<{
  enrollment: UserEnrollment
  onContinue: (enrollment: UserEnrollment) => void
}> = ({ enrollment, onContinue }) => {
  const theme = useTheme()
  const assignment = enrollment.assignment
  
  if (!assignment) return null

  const isCourse = assignment.content_type === 'course'

  const getContentTitle = () => {
    if (isCourse && typeof assignment.course === 'object' && assignment.course?.title) {
      return assignment.course.title;
    }
    if (!isCourse && typeof assignment.assessment === 'object' && assignment.assessment?.title) {
      return assignment.assessment.title;
    }
    if (assignment.content_name) {
      return assignment.content_name;
    }
    return isCourse ? 'Course' : 'Assessment';
  }

  const getStatusLabel = () => {
    if (isCourse) {
      // For courses: New if assigned with 0 progress, In Progress if has progress
      return (enrollment.status === 'assigned' && (Number(enrollment.progress) || 0) === 0) ? 'New' : 'In Progress'
    } else {
      // For assessments: New if no submission status, In Progress if in progress
      return !enrollment.submission_status ? 'New' : 'In Progress'
    }
  }

  const getButtonText = () => {
    if (isCourse) {
      return getStatusLabel() === 'New' ? 'Start' : 'Continue'
    } else {
      return getStatusLabel() === 'New' ? 'Start' : 'Continue'
    }
  }

  return (
    <ListItem 
      button
      onClick={() => onContinue(enrollment)}
      sx={{ 
        p: 2,
        mb: 1.5,
        borderRadius: 3,
        bgcolor: 'background.paper',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[3],
        }
      }}
      secondaryAction={
        <Button
          variant="contained"
          size="small"
          color={isCourse ? 'primary' : 'secondary'}
          startIcon={<PlayCircle />}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onContinue(enrollment)
          }}
          sx={{ borderRadius: 2 }}
        >
          {getButtonText()}
        </Button>
      }
    >
      <ListItemAvatar>
        <Avatar 
          sx={{ 
            bgcolor: isCourse ? theme.palette.primary.main : theme.palette.secondary.main,
            width: 48,
            height: 48,
          }}
        >
          {isCourse ? <School /> : <Assignment />}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="subtitle1" fontWeight="bold">
            {getContentTitle()}
          </Typography>
        }
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          <Box component="span" display="flex" alignItems="center" gap={2} mt={0.5} minHeight={24}>
            {assignment.is_mandatory && (
              <Chip 
                label="MANDATORY"
                size="small"
                sx={{ 
                  bgcolor: theme.palette.error.main + '20',
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            )}
            {isCourse && (
              <Box sx={{ width: '100px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Number(enrollment.progress) || 0}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    flex: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      bgcolor: theme.palette.primary.main,
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {Math.round(Number(enrollment.progress) || 0)}%
                </Typography>
              </Box>
            )}
            {assignment.due_date && (
              <Typography 
                component="span"
                variant="caption" 
                sx={{
                  color: enrollment.is_overdue 
                    ? theme.palette.error.main 
                    : 'text.secondary',
                  fontWeight: enrollment.is_overdue ? 600 : 400,
                }}
              >
                {enrollment.is_overdue ? 'OVERDUE: ' : 'Due: '}
                {new Date(assignment.due_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}
              </Typography>
            )}
          </Box>
        }
      />
    </ListItem>
  )
}

const ActivityChart: React.FC = () => {
  return <LearningActivityChart />
}

const ModernDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const theme = useTheme()
  const { user } = useSelector((state: RootState) => state.auth)
  // Backend now returns only current user's enrollments via only_mine=true parameter
  const { userEnrollments, isLoading: assignmentsLoading } = useSelector(
    (state: RootState) => state.assignments
  )

  useEffect(() => {
    dispatch(fetchUserEnrollments())
    dispatch(fetchAssessments())
  }, [dispatch])

  const handleAssignmentClick = async (enrollment: UserEnrollment) => {
    if (!enrollment.assignment) {
      console.error('Assignment data is missing for enrollment:', enrollment.id)
      return
    }
    
    const assignment = enrollment.assignment
    
    if (assignment.content_type === 'assessment' && enrollment.submission_status === 'graded') {
      return
    }
    
    // Get navigation details first
    let navigationPath = ''
    let contentId: any = null

    const getCourseId = (a: any) => {
      if (!a) return null
      // Prefer explicit numeric/string id on the 'course' field
      if (a.course && (typeof a.course === 'number' || typeof a.course === 'string')) return a.course
      // If 'course' is an object with an id property, use it
      if (a.course && typeof a.course === 'object' && a.course.id) return a.course.id
      // Fallback to 'course_detail' if present (some serializers nest details there)
      if (a.course_detail && a.course_detail.id) return a.course_detail.id
      return null
    }

    const getAssessmentId = (a: any) => {
      if (!a) return null
      if (a.assessment && (typeof a.assessment === 'number' || typeof a.assessment === 'string')) return a.assessment
      if (a.assessment && typeof a.assessment === 'object' && a.assessment.id) return a.assessment.id
      if (a.assessment_detail && a.assessment_detail.id) return a.assessment_detail.id
      return null
    }

    if (assignment.content_type === 'assessment') {
      contentId = getAssessmentId(assignment)
      navigationPath = contentId ? `/assessments/${contentId}` : ''
    } else if (assignment.content_type === 'course') {
      contentId = getCourseId(assignment)
      navigationPath = contentId ? `/courses/${contentId}` : ''
    }
    
    if (!contentId || !navigationPath) {
      console.error('🔥 Cannot navigate - missing content ID or path', { assignment, contentId })
      return
    }
    
    // Start assignment if needed (but don't let it block navigation)
    if (enrollment.status === 'assigned') {
      // Fire and forget - don't await this
      dispatch(startAssignment(enrollment.id.toString()))
        .unwrap()
        .then(() => {})
        .catch((error) => console.error('🔥 Failed to start assignment (but continuing anyway):', error))
    }
    
    // Always navigate, regardless of start assignment result
    navigate(navigationPath)
  }

  // Statistics calculations
  // Deduplicate all enrollments first to ensure consistent stats across all widgets
  const uniqueEnrollmentsMap = new Map<string, UserEnrollment>();
  
  (userEnrollments || []).forEach(enrollment => {
    if (!enrollment.assignment) return;
    
    let contentId = '';
    // Safely get content ID handling both object and ID references
    try {
      const getCourseId = (a: any) => {
        if (!a) return null
        if (a.course && (typeof a.course === 'number' || typeof a.course === 'string')) return a.course
        if (a.course && typeof a.course === 'object' && a.course.id) return a.course.id
        if (a.course_detail && a.course_detail.id) return a.course_detail.id
        return null
      }

      const getAssessmentId = (a: any) => {
        if (!a) return null
        if (a.assessment && (typeof a.assessment === 'number' || typeof a.assessment === 'string')) return a.assessment
        if (a.assessment && typeof a.assessment === 'object' && a.assessment.id) return a.assessment.id
        if (a.assessment_detail && a.assessment_detail.id) return a.assessment_detail.id
        return null
      }

      if (enrollment.assignment.content_type === 'course') {
        const courseId = getCourseId(enrollment.assignment)
        if (courseId) contentId = `course_${courseId}`;
      } else if (enrollment.assignment.content_type === 'assessment') {
        const assessmentId = getAssessmentId(enrollment.assignment)
        if (assessmentId) contentId = `assessment_${assessmentId}`;
      }
    } catch (e) {
      console.warn('Error processing enrollment for deduplication:', e);
      return;
    }
    
    if (!contentId) return;
    
    const existing = uniqueEnrollmentsMap.get(contentId);
    
    // Logic to pick the "best" enrollment (highest progress or completed status)
    if (!existing) {
      uniqueEnrollmentsMap.set(contentId, enrollment);
    } else {
      const currentProgress = Number(enrollment.progress) || 0;
      const existingProgress = Number(existing.progress) || 0;
      
      // Prefer completed
      if (enrollment.status === 'completed' && existing.status !== 'completed') {
        uniqueEnrollmentsMap.set(contentId, enrollment);
      }
      // Prefer higher progress
      else if (currentProgress > existingProgress) {
        uniqueEnrollmentsMap.set(contentId, enrollment);
      }
      // Prefer in_progress over assigned
      else if (enrollment.status === 'in_progress' && existing.status === 'assigned') {
        uniqueEnrollmentsMap.set(contentId, enrollment);
      }
      // If everything else is equal, prefer the one with a more recent ID (likely newer)
      else if (currentProgress === existingProgress && enrollment.status === existing.status) {
        if (enrollment.id > existing.id) {
          uniqueEnrollmentsMap.set(contentId, enrollment);
        }
      }
    }
  });
  
  const uniqueEnrollments = Array.from(uniqueEnrollmentsMap.values());

  // Filter to only include courses and standalone assessments (excluding course-embedded assessments)
  const filteredEnrollments = uniqueEnrollments.filter(e => {
    if (e.content_type === 'course') return true
    if (e.content_type === 'assessment') return e.is_standalone_assessment === true
    return false
  })
  
  const totalCourses = filteredEnrollments.filter(e => e.content_type === 'course' && e.status !== 'completed' && (Number(e.progress) || 0) < 100).length
  const totalAssessments = filteredEnrollments.filter(e => e.content_type === 'assessment' && e.is_standalone_assessment && e.status !== 'completed').length
  const completedCertifications = filteredEnrollments.filter(e => e.status === 'completed').length
  
  // Calculate average progress based on actual progress percentage
  const totalProgress = filteredEnrollments.reduce((sum, e) => sum + (Number(e.progress) || 0), 0)
  const averageProgress = filteredEnrollments.length > 0 
    ? Math.round(totalProgress / filteredEnrollments.length)
    : 0

  if (assignmentsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography>Loading your dashboard...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Section */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12}>
          <WelcomeCard userName={user?.first_name || 'Student'} />
        </Grid>
      </Grid>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Courses"
            value={totalCourses}
            icon={<School sx={{ color: theme.palette.info.main }} />}
            color={theme.palette.info.main}
            helpText="Total number of courses you have access to, excluding completed courses"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Assessments"
            value={totalAssessments}
            icon={<Assignment sx={{ color: theme.palette.secondary.main }} />}
            color={theme.palette.secondary.main}
            helpText="Total number of standalone assessments you have access to, excluding completed assessments"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Certifications"
            value={completedCertifications}
            icon={<EmojiEvents sx={{ color: theme.palette.success.main }} />}
            color={theme.palette.success.main}
            helpText="Total number of completed courses and assessments (certifications earned)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Average Progress"
            value={`${averageProgress}%`}
            icon={<TrendingUp sx={{ color: theme.palette.primary.main }} />}
            color={theme.palette.primary.main}
            helpText="Percentage of completed courses and assessments"
          />
        </Grid>
      </Grid>

      {/* Learning Content - Side by Side Layout */}
      <Grid container spacing={3}>
        {/* Courses Section */}
        <Grid item xs={12} lg={6}>
          {/* Courses (Available and In Progress) */}
          {(() => {
            const allCourses = uniqueEnrollments.filter(enrollment => 
              enrollment.assignment && 
              enrollment.assignment.content_type === 'course' && 
              enrollment.status !== 'completed' && 
              (Number(enrollment.progress) || 0) < 100
            ).sort((a, b) => {
              // Sort: In Progress first, then New
              const aIsInProgress = a.status === 'in_progress' && (Number(a.progress) || 0) > 0
              const bIsInProgress = b.status === 'in_progress' && (Number(b.progress) || 0) > 0
              
              if (aIsInProgress && !bIsInProgress) return -1
              if (!aIsInProgress && bIsInProgress) return 1
              return 0
            })
            
            // Debug logging is only enabled on localhost or when VITE_ENABLE_VERBOSE_LOGS=true
            // Use the shared logger so we can suppress verbose output in non-dev environments
            // (importing lazily to avoid changing module boundaries)
            logger.debug('🔍 DEBUG: allCourses for display:', allCourses.length, allCourses)
            
            return allCourses.length > 0 ? (
              <Box mb={3}>
                <Typography variant="h5" fontWeight="bold" mb={2}>
                  Courses
                </Typography>
                <List sx={{ p: 0 }}>
                  {allCourses.map((enrollment) => (
                    <AvailableContentItem
                      key={enrollment.id}
                      enrollment={enrollment}
                      onContinue={handleAssignmentClick}
                    />
                  ))}
                </List>
              </Box>
            ) : (
              <Box mb={3}>
                <Card sx={{ textAlign: 'center', py: 4 }}>
                  <CardContent>
                    <School sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" mb={1}>
                      No courses available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check back later for new courses!
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )
          })()}
        </Grid>

        {/* Assessments Section */}
        <Grid item xs={12} lg={6}>
          {/* Assessments (Available and In Progress) */}
          {(() => {
            const allAssessments = uniqueEnrollments.filter(enrollment => 
              enrollment.assignment && 
              enrollment.assignment.content_type === 'assessment' && 
              enrollment.is_standalone_assessment &&
              (!enrollment.submission_status || enrollment.submission_status === 'in_progress')
            ).sort((a, b) => {
              // Sort: In Progress first, then New
              const aIsInProgress = a.submission_status === 'in_progress'
              const bIsInProgress = b.submission_status === 'in_progress'
              
              if (aIsInProgress && !bIsInProgress) return -1
              if (!aIsInProgress && bIsInProgress) return 1
              return 0
            })
            
            return allAssessments.length > 0 ? (
              <Box mb={3}>
                <Typography variant="h5" fontWeight="bold" mb={2}>
                  Assessments
                </Typography>
                <List sx={{ p: 0 }}>
                  {allAssessments.map((enrollment) => (
                    <AvailableContentItem
                      key={enrollment.id}
                      enrollment={enrollment}
                      onContinue={handleAssignmentClick}
                    />
                  ))}
                </List>
              </Box>
            ) : (
              <Box mb={3}>
                <Card sx={{ textAlign: 'center', py: 4 }}>
                  <CardContent>
                    <Assignment sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" mb={1}>
                      No assessments available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check back later for new assessments!
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )
          })()}
        </Grid>
      </Grid>

      {/* Learning Activity Chart - Moved to bottom */}
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} lg={6}>
          <ActivityChart />
        </Grid>
        <Grid item xs={12} lg={6}>
          <ProgressChart />
        </Grid>
      </Grid>
    </Box>
  )
}

export default ModernDashboardPage